import cx from 'classnames'
import {
  InfoSectionItems,
  InfoSectionItem,
} from '@/features/admin/components/InfoSectionItems'
import type { WorkspacePostingStatusVariant } from '@/features/admin/staffing/workspace-posting-formatters'
import type {
  StaffingDetailsApplicant,
  StaffingDetailsPosting,
} from './StaffingDetailsViewModel'
import { Button } from '@/components/Button'

import { IconButton } from '@/components/IconButton'
import {
  ImageCell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TextCell,
} from '@/components/Table'
import styles from './StaffingDetailsPanel.module.css'
import { match } from '@/utils/pattern-match-util'
import { byDesign } from '@/infra/design'
import imgPlaneMessageUrl from '@/assets/misc/img_plane-message.svg'
import LookingToInviteIllustration from '@/components/SpotIllustration/LookingToInviteIllustration'

export interface StaffingDetailsPanelProps {
  onClose: () => unknown
  /**
   * `null` when no posting is selected.
   */
  posting: StaffingDetailsPosting | null
  applicantsExpanded: boolean
  onToggleApplicantsExpanded: () => unknown
  showManageButton: boolean
  onManage: () => unknown
  showActions: boolean
  showDraftActions: boolean
  showMarkInReview: boolean
  showInReviewActions: boolean
  onUpdateDraft: () => unknown
  onPublish: () => unknown
  isPublishPending: boolean
  onMarkInReview: () => unknown
  isMarkInReviewPending: boolean
  onReactivate: () => unknown
  isReactivatePending: boolean
  onManuallyClose: () => unknown
  isManuallyClosePending: boolean
}

export function StaffingDetailsPanel({
  onClose,
  posting,
  applicantsExpanded,
  onToggleApplicantsExpanded,
  showManageButton,
  onManage,
  showActions,
  showDraftActions,
  showMarkInReview,
  showInReviewActions,
  onUpdateDraft,
  onPublish,
  isPublishPending,
  onMarkInReview,
  isMarkInReviewPending,
  onReactivate,
  isReactivatePending,
  onManuallyClose,
  isManuallyClosePending,
}: StaffingDetailsPanelProps) {
  return (
    <div className={styles.root}>
      <StaffingDetailsHeader onClose={onClose} />
      <StaffingDetailsInfoSection
        posting={posting}
        applicantsExpanded={applicantsExpanded}
      />
      <StaffingDetailsApplicantsSection
        posting={posting}
        applicantsExpanded={applicantsExpanded}
        onToggleApplicantsExpanded={onToggleApplicantsExpanded}
        showManageButton={showManageButton}
        onManage={onManage}
      />
      {showActions && (
        <StaffingDetailsActionFooter
          posting={posting}
          showDraftActions={showDraftActions}
          showMarkInReview={showMarkInReview}
          showInReviewActions={showInReviewActions}
          onUpdateDraft={onUpdateDraft}
          onPublish={onPublish}
          isPublishPending={isPublishPending}
          onMarkInReview={onMarkInReview}
          isMarkInReviewPending={isMarkInReviewPending}
          onReactivate={onReactivate}
          isReactivatePending={isReactivatePending}
          onManuallyClose={onManuallyClose}
          isManuallyClosePending={isManuallyClosePending}
        />
      )}
    </div>
  )
}

interface StaffingDetailsHeaderProps {
  onClose: () => unknown
}

function StaffingDetailsHeader({ onClose }: StaffingDetailsHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.mainContainer}>
        <IconButton
          name="back"
          label="Close details"
          onPress={onClose}
          size={24}
          color="var(--grey600)"
        />
      </div>
    </div>
  )
}

interface StaffingDetailsInfoSectionProps {
  posting: StaffingDetailsPosting | null
  applicantsExpanded: boolean
}

function StaffingDetailsInfoSection({
  posting,
  applicantsExpanded,
}: StaffingDetailsInfoSectionProps) {
  const applicantsContracted = !applicantsExpanded

  if (!posting) {
    return null
  }

  return (
    <div
      className={cx(
        styles.info,
        applicantsContracted && styles.applicantsContracted,
      )}
    >
      <h2 className={styles.infoTitle}>{posting.workspaceName}</h2>
      <InfoSectionItems>
        <InfoSectionItem label="Location">{posting.location}</InfoSectionItem>
        <InfoSectionItem label="Primary software">
          {posting.primarySoftware}
          {posting.backupFileNeeded && (
            <div className={styles.backupFileNeeded}>with backup files</div>
          )}
        </InfoSectionItem>
        <InfoSectionItem label="Supplemental">
          {posting.supplementalSoftware}
        </InfoSectionItem>
        <InfoSectionItem label="Pros required">
          {posting.capacity}
        </InfoSectionItem>
        <InfoSectionItem label="Requirements">
          {posting.specialtiesText}
        </InfoSectionItem>
        <InfoSectionItem label="Type of work">
          {posting.typeOfWorkText}
        </InfoSectionItem>
        <InfoSectionItem label="Description">
          {posting.description}
        </InfoSectionItem>
        <InfoSectionItem label="Status">
          <StatusText
            displayStatus={posting.displayStatus}
            variant={posting.statusVariant}
          />
        </InfoSectionItem>
      </InfoSectionItems>
    </div>
  )
}

interface StaffingDetailsApplicantsSectionProps {
  posting: StaffingDetailsPosting | null
  applicantsExpanded: boolean
  onToggleApplicantsExpanded: () => unknown
  showManageButton: boolean
  onManage: () => unknown
}

function StaffingDetailsApplicantsSection({
  posting,
  applicantsExpanded,
  onToggleApplicantsExpanded,
  showManageButton,
  onManage,
}: StaffingDetailsApplicantsSectionProps) {
  const contracted = !applicantsExpanded

  if (!posting) {
    return null
  }

  const emptyState = posting.applicants.length === 0

  return (
    <div
      className={cx(
        styles.applicantSection,
        emptyState && styles.emptyState,
        contracted && styles.contracted,
      )}
    >
      {emptyState ? (
        <>
          <div className={styles.emptyStateImageContainer}>
            {byDesign(
              <img
                src={imgPlaneMessageUrl}
                alt="Paper plane delivering an envelope."
              />,
              <LookingToInviteIllustration
                className={styles.emptyStateIllustration}
                label="Paper plane delivering an envelope."
              />,
            )}
          </div>
          <div className={styles.emptyStateTextContainer}>
            <h2 className={styles.emptyStateTitle}>Post the opportunity</h2>
            <p className={styles.emptyStateDescription}>
              Once the post is shared, all applicants will be listed here for
              review.
            </p>
          </div>
        </>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <IconButton
              name="arrow"
              label="Toggle applicants"
              className={styles.sectionAccordion}
              onPress={onToggleApplicantsExpanded}
              size={24}
              color="var(--grey600)"
            />
            <div className={styles.sectionTitle}>
              Applicants
              <span className={styles.sectionTitleSuffix}>
                {` (${posting.applicants.length})`}
              </span>
            </div>
            {showManageButton && (
              <div className={styles.manageButton}>
                <Button label="Manage" variant="text" onPress={onManage} />
              </div>
            )}
          </div>
          <div className={styles.sectionContent}>
            <Table className={styles.table} stickyHeader>
              <TableHead className={styles.tableHead}>
                <TableHeader className={styles.avatarColumn}>
                  <div className={styles.spacer} />
                </TableHeader>
                <TableHeader align="left">Full name</TableHeader>
                <TableHeader align="right">Status</TableHeader>
              </TableHead>
              <TableBody className={styles.tableBody}>
                {posting.applicants.map((item) => (
                  <ApplicantRow applicant={item} key={item.userPublicId} />
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  )
}

interface StaffingDetailsActionFooterProps {
  posting: StaffingDetailsPosting | null
  showDraftActions: boolean
  showMarkInReview: boolean
  showInReviewActions: boolean
  onUpdateDraft: () => unknown
  onPublish: () => unknown
  isPublishPending: boolean
  onMarkInReview: () => unknown
  isMarkInReviewPending: boolean
  onReactivate: () => unknown
  isReactivatePending: boolean
  onManuallyClose: () => unknown
  isManuallyClosePending: boolean
}

function StaffingDetailsActionFooter({
  posting,
  showDraftActions,
  showMarkInReview,
  showInReviewActions,
  onUpdateDraft,
  onPublish,
  isPublishPending,
  onMarkInReview,
  isMarkInReviewPending,
  onReactivate,
  isReactivatePending,
  onManuallyClose,
  isManuallyClosePending,
}: StaffingDetailsActionFooterProps) {
  if (!posting) {
    return null
  }

  const isInReviewActionPending = isReactivatePending || isManuallyClosePending

  return (
    <div className={styles.footer}>
      {showDraftActions && (
        <div className={styles.actions}>
          <Button
            label="Edit"
            isDisabled={isPublishPending}
            onPress={onUpdateDraft}
          />
          <Button
            label="Submit Post"
            variant="primary"
            isPending={isPublishPending}
            isDisabled={isPublishPending}
            onPress={onPublish}
          />
        </div>
      )}
      {showMarkInReview && (
        <Button
          label="Review Post"
          variant="primary"
          isPending={isMarkInReviewPending}
          isDisabled={isMarkInReviewPending}
          onPress={onMarkInReview}
        />
      )}
      {showInReviewActions && (
        <div className={styles.actions}>
          <Button
            label="Reactivate"
            isPending={isReactivatePending}
            isDisabled={isInReviewActionPending}
            onPress={onReactivate}
          />
          <Button
            label="Close Post"
            variant="primary"
            isPending={isManuallyClosePending}
            isDisabled={isInReviewActionPending}
            onPress={onManuallyClose}
          />
        </div>
      )}
    </div>
  )
}

interface StatusTextProps {
  displayStatus: string
  variant: WorkspacePostingStatusVariant
}

function StatusText({ displayStatus, variant }: StatusTextProps) {
  return (
    <div
      className={match(variant, {
        Active: () => styles.statusActive,
        Closed: () => styles.statusClosed,
        Draft: () => styles.statusDraft,
        InReview: () => styles.statusInReview,
        Unspecified: () => undefined,
      })}
    >
      {displayStatus}
    </div>
  )
}

interface ApplicantProps {
  applicant: StaffingDetailsApplicant
}

function ApplicantRow({ applicant }: ApplicantProps) {
  return (
    <TableRow className={styles.applicantRow}>
      <ImageCell image={applicant.avatar} size={24} noPadding />
      <TextCell>
        <div className={styles.applicantName}>{applicant.displayName}</div>
      </TextCell>
      <ApplicantStatusTextCell applicant={applicant} />
    </TableRow>
  )
}

function ApplicantStatusTextCell({ applicant }: ApplicantProps) {
  return (
    <TextCell>
      <div
        className={cx(
          styles.applicantStatus,
          match(applicant.statusVariant, {
            Accepted: () => styles.applicantStatusAccepted,
            Applied: () => styles.applicantStatusApplied,
            Invited: () => styles.applicantStatusInvited,
            Expired: () => styles.applicantStatusExpired,
            Rejected: () => styles.applicantStatusRejected,
            Withdrawn: () => styles.applicantStatusWithdrawn,
            Unspecified: () => undefined,
          }),
        )}
      >
        {applicant.displayStatus}
      </div>
    </TextCell>
  )
}
